import React, { useState } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import { KeyedMutator } from 'swr/_internal';
import { Alert, AlertTitle, Button, Grid, Link as MuiLink, Typography } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { AccountData, AccountService, ApiError, OrderPageData } from '../../../api';
import { UseAuthPermissions } from '../../../session/UseAuthPermissions';
import ConfirmationDialog from '../../../common/ui/ConfimationDialog';
import { useLanguage } from '../../../common/GeneralUtils';
import { useSaveHeader } from '../../saveHeader/SaveHeaderContext';
import { SettingsRow } from '../../formComponents/SettingsRow';
import { getPassOrderPagesUsingOrderPage } from '../../../utils/AccountUtils';

/** The API refuses to archive a page that still has active orders dated today or later. */
const ARCHIVE_ERROR_MESSAGE_KEYS: Record<string, string> = {
  cannotArchiveOrderPageWithFutureOrders: 'deleteOrderPage.errorFutureOrders',
};

const resolveArchiveError = (e: unknown, t: TFunction): string => {
  const detail = (e as ApiError)?.body?.detail as string | undefined;
  const messageKey = detail ? ARCHIVE_ERROR_MESSAGE_KEYS[detail] : undefined;
  return messageKey ? t(messageKey) : (e as Error)?.message || t('saveHeader.errorGeneric');
};

type RestrictionsSettingsSectionProps = {
  orderPage: OrderPageData;
  updateLocalAccount: KeyedMutator<AccountData>;
  account: AccountData;
};

export const DeletionSection = ({ orderPage, updateLocalAccount, account }: RestrictionsSettingsSectionProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { readOnly } = UseAuthPermissions();
  const posthog = usePostHog();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const language = useLanguage();
  const { showSuccess, showError } = useSaveHeader();

  const blockingPasses = getPassOrderPagesUsingOrderPage(account, orderPage.id);
  const isBlocked = blockingPasses.length > 0;

  const handleDelete = async () => {
    try {
      posthog.capture('Editing settings', { section: 'DeletionSection', orderPageId: orderPage.id });
      account = await AccountService.archiveOrderPage(orderPage.id);
      await updateLocalAccount(account);
      navigate(`/dashboard/order-pages`);
      showSuccess();
      setIsDialogOpen(false);
    } catch (e) {
      showError(resolveArchiveError(e, t));
    }
  };

  return (
    <>
      <SettingsRow label={t('deleteOrderPage.title')} isLast>
        <Grid container flexDirection="column" alignItems="flex-start" gap={2}>
          {isBlocked && (
            <Alert severity="warning">
              <AlertTitle>{t('deleteOrderPage.blockedByPassesTitle')}</AlertTitle>
              <Typography>{t('deleteOrderPage.blockedByPassesText', { name: orderPage.label[language] })}</Typography>
              <Grid container flexDirection="column" mt={1}>
                {blockingPasses.map((passOrderPage) => (
                  <MuiLink
                    key={passOrderPage.id}
                    component={Link}
                    to={`/dashboard/pass-settings/${passOrderPage.id}`}
                    underline="hover">
                    {passOrderPage.label[language]}
                  </MuiLink>
                ))}
              </Grid>
            </Alert>
          )}
          <Button
            variant="contained"
            color="error"
            onClick={() => setIsDialogOpen(true)}
            disabled={readOnly || isBlocked}>
            {t('deleteOrderPage.button', { name: orderPage.label[language] })}
          </Button>
        </Grid>
      </SettingsRow>
      <ConfirmationDialog
        isOpen={isDialogOpen}
        onClose={(actionConfirmed) => {
          if (actionConfirmed) {
            void handleDelete();
          }
          setIsDialogOpen(false);
        }}
        title={t('deleteOrderPage.confirmTitle')}
        content={<Typography>{t('deleteOrderPage.confirmText', { name: orderPage.label[language] })}</Typography>}
        ctaText={t('deleteOrderPage.confirm')}
        confirmButtonColor="error"
      />
    </>
  );
};
